import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { motion, Reorder } from "framer-motion";
import {
  Lock, LogOut, Mail, Briefcase, Loader2, Inbox, Download,
  MessageSquareQuote, Users, Plus, Pencil, Trash2, Search, Save, X, Star, Layers, GripVertical, Upload, StickyNote, UserRound, UserPlus, ShieldCheck, History, Palette, FileText, ReceiptText, Eye, EyeOff, KeyRound, ArrowLeft,
} from "lucide-react";
import { toast } from "sonner";
import { LOGO_URL } from "./data";
import { AppearanceTab } from "./AppearanceTab";
import { ContentTab } from "./ContentTab";
import { InvoicesTab } from "./InvoicesTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY = "pl_admin_token";

const PasswordField = ({ value, onChange, placeholder, testid, autoComplete, className = "", inputClass }) => {
  const [show, setShow] = useState(false);
  return (
    <div className={`relative ${className}`}>
      <input
        data-testid={testid}
        type={show ? "text" : "password"}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        autoComplete={autoComplete}
        className={`${inputClass || "w-full rounded-2xl border border-border bg-white px-5 py-3.5 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-[border-color,box-shadow]"} pr-11`}
      />
      <button
        type="button"
        data-testid={`${testid}-toggle`}
        onClick={() => setShow((s) => !s)}
        tabIndex={-1}
        aria-label={show ? "Hide password" : "Show password"}
        className="absolute right-3.5 top-1/2 -translate-y-1/2 text-foreground/40 hover:text-primary transition-colors"
      >
        {show ? <EyeOff size={16} /> : <Eye size={16} />}
      </button>
    </div>
  );
};

export const AdminDashboard = () => {
  const [username, setUsername] = useState("");
  const [pw, setPw] = useState("");
  const [authed, setAuthed] = useState(false);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(false);
  const [section, setSection] = useState("leads");

  const [enquiries, setEnquiries] = useState([]);
  const [applications, setApplications] = useState([]);
  const [stats, setStats] = useState({ enquiries: 0, applications: 0 });
  const [invStats, setInvStats] = useState(null);
  const [tab, setTab] = useState("enquiries");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [enqStatusFilter, setEnqStatusFilter] = useState("all");

  const [testimonials, setTestimonials] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [services, setServices] = useState([]);
  const [team, setTeam] = useState([]);
  const [users, setUsers] = useState([]);
  const [newUser, setNewUser] = useState({ username: "", password: "", role: "admin", email: "" });
  const [creatingUser, setCreatingUser] = useState(false);
  const [emailEdit, setEmailEdit] = useState(null); // {id, value}
  const [savingEmail, setSavingEmail] = useState(false);
  const [authView, setAuthView] = useState("login"); // login | forgot | reset
  const [fp, setFp] = useState({ username: "", otp: "", new_password: "", hint: "" });
  const [fpLoading, setFpLoading] = useState(false);
  const [cpw, setCpw] = useState({ current_password: "", new_password: "" });
  const [changingPw, setChangingPw] = useState(false);
  const [audit, setAudit] = useState([]);
  const [settings, setSettings] = useState({ email: "", phone: "", location: "", instagram: "", linkedin: "" });
  const [savingSettings, setSavingSettings] = useState(false);
  const [editing, setEditing] = useState(null); // {type, data}
  const [uploading, setUploading] = useState(false);
  const [sendingDigest, setSendingDigest] = useState(false);

  const sendDigest = async () => {
    setSendingDigest(true);
    try {
      const res = await axios.post(`${API}/admin/send-digest`, {}, auth());
      toast.success(`Weekly summary emailed to ${res.data.sent_to}.`);
    } catch (err) {
      toast.error("Couldn't send the summary.");
    } finally {
      setSendingDigest(false);
    }
  };

  const uploadImage = async (fileList) => {
    const f = fileList && fileList[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const res = await axios.post(`${API}/admin/upload`, fd, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem(KEY) || ""}`, "Content-Type": "multipart/form-data" },
      });
      const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${res.data.url}`;
      setEditing((prev) => ({ ...prev, data: { ...prev.data, image: fullUrl } }));
      toast.success("Image uploaded.");
    } catch (err) {
      toast.error("Image upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const tokenRef = () => sessionStorage.getItem(KEY) || "";
  const auth = useCallback(() => ({ headers: { Authorization: `Bearer ${tokenRef()}` } }), []);

  const loadAll = useCallback(async (token) => {
    const h = { headers: { Authorization: `Bearer ${token}` } };
    const [e, a, s, t, j, sv, tm, us, au, st] = await Promise.all([
      axios.get(`${API}/admin/enquiries`, h),
      axios.get(`${API}/admin/applications`, h),
      axios.get(`${API}/admin/stats`, h),
      axios.get(`${API}/testimonials`),
      axios.get(`${API}/jobs`),
      axios.get(`${API}/services`),
      axios.get(`${API}/team`),
      axios.get(`${API}/admin/users`, h),
      axios.get(`${API}/admin/audit`, h),
      axios.get(`${API}/settings`),
    ]);
    setEnquiries(e.data);
    setApplications(a.data);
    setStats(s.data);
    setTestimonials(t.data);
    setJobs(j.data);
    setServices(sv.data);
    setTeam(tm.data);
    setUsers(us.data);
    setAudit(au.data);
    setSettings(st.data);
    try {
      const iv = await axios.get(`${API}/admin/invoice-stats`, h);
      setInvStats(iv.data);
    } catch (e) { /* viewer role or unavailable */ }
  }, []);

  const login = useCallback(async (uname, password) => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { username: uname, password });
      const token = res.data.token;
      sessionStorage.setItem(KEY, token);
      setMe(res.data.user);
      await loadAll(token);
      setAuthed(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid username or password.");
      sessionStorage.removeItem(KEY);
    } finally {
      setLoading(false);
    }
  }, [loadAll]);

  const resume = useCallback(async (token) => {
    setLoading(true);
    try {
      const meRes = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
      setMe(meRes.data);
      await loadAll(token);
      setAuthed(true);
    } catch (err) {
      sessionStorage.removeItem(KEY);
    } finally {
      setLoading(false);
    }
  }, [loadAll]);

  useEffect(() => {
    const saved = sessionStorage.getItem(KEY);
    if (saved) resume(saved);
  }, [resume]);

  const logout = () => {
    sessionStorage.removeItem(KEY);
    setAuthed(false);
    setMe(null);
    setUsername("");
    setPw("");
  };

  const refreshUsers = async () => setUsers((await axios.get(`${API}/admin/users`, auth())).data);
  const refreshAudit = async () => setAudit((await axios.get(`${API}/admin/audit`, auth())).data);

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.put(`${API}/admin/settings`, settings, auth());
      toast.success("Contact details saved.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't save contact details.");
    } finally {
      setSavingSettings(false);
    }
  };

  const goSection = (id) => {
    setSection(id);
    if (id === "activity") refreshAudit();
  };

  const clearAudit = async () => {
    if (!window.confirm("Clear the entire activity log? This can't be undone.")) return;
    try {
      await axios.delete(`${API}/admin/audit`, auth());
      setAudit([]);
      toast.success("Activity log cleared.");
    } catch (err) {
      toast.error("Couldn't clear the log.");
    }
  };

  const createUser = async () => {
    if (!newUser.username || !newUser.password) return toast.error("Username and password are required.");
    setCreatingUser(true);
    try {
      await axios.post(`${API}/admin/users`, newUser, auth());
      setNewUser({ username: "", password: "", role: "admin", email: "" });
      await refreshUsers();
      toast.success("User created.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't create user.");
    } finally {
      setCreatingUser(false);
    }
  };

  const changeMyPassword = async () => {
    if (!cpw.current_password || !cpw.new_password) return toast.error("Fill in both password fields.");
    setChangingPw(true);
    try {
      await axios.post(`${API}/auth/change-password`, cpw, auth());
      setCpw({ current_password: "", new_password: "" });
      toast.success("Password updated.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't update password.");
    } finally {
      setChangingPw(false);
    }
  };

  const deleteUser = async (uid) => {
    try {
      await axios.delete(`${API}/admin/users/${uid}`, auth());
      await refreshUsers();
      toast.success("User removed.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't remove user.");
    }
  };

  const requestReset = async () => {
    if (!fp.username.trim()) return toast.error("Enter your username first.");
    setFpLoading(true);
    try {
      const res = await axios.post(`${API}/auth/forgot-password`, { username: fp.username });
      setFp((p) => ({ ...p, hint: res.data.email_hint || "" }));
      setAuthView("reset");
      toast.success(res.data.message || "Reset code sent.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't send the reset code.");
    } finally {
      setFpLoading(false);
    }
  };

  const submitReset = async () => {
    if (!fp.otp.trim() || !fp.new_password) return toast.error("Enter the code and a new password.");
    setFpLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { username: fp.username, otp: fp.otp, new_password: fp.new_password });
      toast.success("Password updated. Sign in with your new password.");
      setUsername(fp.username);
      setFp({ username: "", otp: "", new_password: "", hint: "" });
      setAuthView("login");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't reset the password.");
    } finally {
      setFpLoading(false);
    }
  };

  const saveUserEmail = async () => {
    if (!emailEdit) return;
    setSavingEmail(true);
    try {
      await axios.put(`${API}/admin/users/${emailEdit.id}/email`, { email: emailEdit.value }, auth());
      await refreshUsers();
      setEmailEdit(null);
      toast.success("Email updated.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't update email.");
    } finally {
      setSavingEmail(false);
    }
  };

  const refreshTestimonials = async () => setTestimonials((await axios.get(`${API}/testimonials`)).data);
  const refreshJobs = async () => setJobs((await axios.get(`${API}/jobs`)).data);
  const refreshServices = async () => setServices((await axios.get(`${API}/services`)).data);
  const refreshTeam = async () => setTeam((await axios.get(`${API}/team`)).data);

  const updateEnquiryStatus = async (eid, status) => {
    setEnquiries((prev) => prev.map((r) => (r.id === eid ? { ...r, status } : r)));
    try {
      await axios.put(`${API}/admin/enquiries/${eid}/status`, { status }, auth());
      toast.success(`Marked ${status}.`);
    } catch (err) {
      toast.error("Status update failed.");
    }
  };

  const persistOrder = async (type, arr) => {
    if (!isEditor) return;
    try {
      await Promise.all(
        arr.map((item, idx) => {
          if (type === "service")
            return axios.put(`${API}/admin/services/${item.id}`, { no: item.no, title: item.title, desc: item.desc, tags: item.tags, image: item.image, order: idx }, auth());
          if (type === "testimonial")
            return axios.put(`${API}/admin/testimonials/${item.id}`, { quote: item.quote, name: item.name, role: item.role, image: item.image || "", featured: !!item.featured, order: idx }, auth());
          if (type === "team")
            return axios.put(`${API}/admin/team/${item.id}`, { name: item.name, role: item.role, bio: item.bio || "", image: item.image || "", order: idx }, auth());
          return axios.put(`${API}/admin/jobs/${item.id}`, { role: item.role, location: item.location, type: item.type, image: item.image || "", order: idx }, auth());
        })
      );
    } catch (err) {
      toast.error("Couldn't save new order.");
    }
  };

  const updateStatus = async (aid, status) => {
    setApplications((prev) => prev.map((r) => (r.id === aid ? { ...r, status } : r)));
    try {
      await axios.put(`${API}/admin/applications/${aid}/status`, { status }, auth());
      toast.success(`Marked ${status}.`);
    } catch (err) {
      toast.error("Status update failed.");
    }
  };

  const saveNotes = async (aid, notes) => {
    try {
      await axios.put(`${API}/admin/applications/${aid}/notes`, { notes }, auth());
      setApplications((prev) => prev.map((r) => (r.id === aid ? { ...r, notes } : r)));
      toast.success("Note saved.");
    } catch (err) {
      toast.error("Couldn't save note.");
    }
  };

  const saveEnquiryNotes = async (eid, notes) => {
    try {
      await axios.put(`${API}/admin/enquiries/${eid}/notes`, { notes }, auth());
      setEnquiries((prev) => prev.map((r) => (r.id === eid ? { ...r, notes } : r)));
      toast.success("Note saved.");
    } catch (err) {
      toast.error("Couldn't save note.");
    }
  };

  const toggleFeatured = async (t) => {
    try {
      await axios.put(`${API}/admin/testimonials/${t.id}`, { quote: t.quote, name: t.name, role: t.role, image: t.image || "", featured: !t.featured, order: t.order || 0 }, auth());
      await refreshTestimonials();
      toast.success(!t.featured ? "Marked as featured." : "Removed from featured.");
    } catch (err) {
      toast.error("Update failed.");
    }
  };

  const fmt = (iso) => new Date(iso).toLocaleString();

  const inRange = (iso) => {
    const d = new Date(iso);
    if (fromDate && d < new Date(fromDate + "T00:00:00")) return false;
    if (toDate && d > new Date(toDate + "T23:59:59")) return false;
    return true;
  };

  const matchesSearch = (r) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return [r.name, r.email, r.company, r.service, r.message, r.role]
      .filter(Boolean)
      .some((v) => String(v).toLowerCase().includes(q));
  };

  const filteredFor = (id) =>
    (id === "enquiries" ? enquiries : applications).filter(
      (r) =>
        inRange(r.created_at) &&
        matchesSearch(r) &&
        (id === "enquiries" || statusFilter === "all" || (r.status || "New") === statusFilter) &&
        (id === "applications" || enqStatusFilter === "all" || (r.status || "New") === enqStatusFilter)
    );

  const exportCsv = () => {
    const isEnq = tab === "enquiries";
    const data = filteredFor(tab);
    if (data.length === 0) return toast.error("Nothing to export for this view.");
    const cols = isEnq
      ? ["name", "email", "company", "service", "message", "created_at"]
      : ["name", "email", "role", "created_at"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const csv = [cols.join(","), ...data.map((r) => cols.map((c) => esc(r[c])).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `peoplelab-${tab}-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${data.length} ${tab}.`);
  };

  // -------- CRUD --------
  const saveItem = async () => {
    const { type, data } = editing;
    const isNew = !data.id;
    try {
      if (type === "testimonial") {
        const body = { quote: data.quote, name: data.name, role: data.role, image: data.image || "", featured: !!data.featured, order: Number(data.order) || 0 };
        if (!body.quote || !body.name) return toast.error("Quote and name are required.");
        if (isNew) await axios.post(`${API}/admin/testimonials`, body, auth());
        else await axios.put(`${API}/admin/testimonials/${data.id}`, body, auth());
        await refreshTestimonials();
      } else if (type === "job") {
        const body = { role: data.role, location: data.location, type: data.type, image: data.image || "", order: Number(data.order) || 0 };
        if (!body.role || !body.location || !body.type) return toast.error("All fields are required.");
        if (isNew) await axios.post(`${API}/admin/jobs`, body, auth());
        else await axios.put(`${API}/admin/jobs/${data.id}`, body, auth());
        await refreshJobs();
      } else if (type === "team") {
        const body = { name: data.name, role: data.role, bio: data.bio || "", image: data.image || "", order: Number(data.order) || 0 };
        if (!body.name || !body.role) return toast.error("Name and role are required.");
        if (isNew) await axios.post(`${API}/admin/team`, body, auth());
        else await axios.put(`${API}/admin/team/${data.id}`, body, auth());
        await refreshTeam();
      } else {
        const body = {
          no: data.no || "",
          title: data.title,
          desc: data.desc,
          image: data.image || "",
          order: Number(data.order) || 0,
          tags: String(data.tags || "").split(",").map((s) => s.trim()).filter(Boolean),
        };
        if (!body.title || !body.desc) return toast.error("Title and description are required.");
        if (isNew) await axios.post(`${API}/admin/services`, body, auth());
        else await axios.put(`${API}/admin/services/${data.id}`, body, auth());
        await refreshServices();
      }
      toast.success(isNew ? "Added." : "Updated.");
      setEditing(null);
    } catch (err) {
      toast.error("Save failed.");
    }
  };

  const deleteItem = async (type, id) => {
    try {
      if (type === "testimonial") {
        await axios.delete(`${API}/admin/testimonials/${id}`, auth());
        await refreshTestimonials();
      } else if (type === "job") {
        await axios.delete(`${API}/admin/jobs/${id}`, auth());
        await refreshJobs();
      } else if (type === "team") {
        await axios.delete(`${API}/admin/team/${id}`, auth());
        await refreshTeam();
      } else {
        await axios.delete(`${API}/admin/services/${id}`, auth());
        await refreshServices();
      }
      toast.success("Deleted.");
    } catch (err) {
      toast.error("Delete failed.");
    }
  };

  // -------- Login screen --------
  if (!authed) {
    return (
      <div className="min-h-screen bg-background grid place-items-center px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-sm bg-card border border-border rounded-3xl p-8 shadow-[0_30px_70px_-40px_rgba(18,119,110,0.35)]"
        >
          <div className="flex justify-center">
            <img src={LOGO_URL} alt="Peoplelab Advisory" className="h-10 w-auto" data-testid="login-logo" />
          </div>

          {authView === "login" && (
            <>
              <h1 className="mt-6 text-center font-heading text-2xl font-medium">Welcome back</h1>
              <form onSubmit={(e) => { e.preventDefault(); if (username && pw) login(username, pw); }} className="mt-8 space-y-3">
                <input
                  data-testid="admin-username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Username"
                  autoComplete="username"
                  className="w-full rounded-2xl border border-border bg-white px-5 py-3.5 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-[border-color,box-shadow]"
                />
                <PasswordField
                  testid="admin-password"
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  placeholder="Password"
                  autoComplete="current-password"
                />
                <button
                  data-testid="admin-login-btn"
                  type="submit"
                  disabled={loading}
                  className="w-full rounded-full bg-primary text-white py-3.5 text-sm font-medium hover:bg-[#0d5f57] transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {loading ? <Loader2 size={16} className="animate-spin" /> : "Enter dashboard"}
                </button>
              </form>
              <button
                data-testid="forgot-password-link"
                type="button"
                onClick={() => { setFp({ username: username, otp: "", new_password: "", hint: "" }); setAuthView("forgot"); }}
                className="mt-4 block w-full text-center text-xs text-primary/80 hover:text-primary font-medium transition-colors"
              >
                Forgot password?
              </button>
            </>
          )}

          {authView === "forgot" && (
            <>
              <h1 className="mt-6 text-center font-heading text-2xl font-medium">Reset password</h1>
              <p className="mt-2 text-center text-xs text-foreground/55">Enter your username and we'll email you a 6-digit code.</p>
              <form onSubmit={(e) => { e.preventDefault(); requestReset(); }} className="mt-6 space-y-3">
                <input
                  data-testid="fp-username"
                  type="text"
                  value={fp.username}
                  onChange={(e) => setFp({ ...fp, username: e.target.value })}
                  placeholder="Username"
                  autoComplete="username"
                  className="w-full rounded-2xl border border-border bg-white px-5 py-3.5 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-[border-color,box-shadow]"
                />
                <button
                  data-testid="fp-send-code-btn"
                  type="submit"
                  disabled={fpLoading}
                  className="w-full rounded-full bg-primary text-white py-3.5 text-sm font-medium hover:bg-[#0d5f57] transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {fpLoading ? <Loader2 size={16} className="animate-spin" /> : <><KeyRound size={15} /> Send reset code</>}
                </button>
              </form>
              <button
                data-testid="fp-back-to-login"
                type="button"
                onClick={() => setAuthView("login")}
                className="mt-4 w-full text-center text-xs text-foreground/50 hover:text-primary font-medium transition-colors inline-flex items-center justify-center gap-1"
              >
                <ArrowLeft size={12} /> Back to sign in
              </button>
            </>
          )}

          {authView === "reset" && (
            <>
              <h1 className="mt-6 text-center font-heading text-2xl font-medium">Enter your code</h1>
              <p className="mt-2 text-center text-xs text-foreground/55">
                We sent a 6-digit code to <b>{fp.hint || "your email"}</b>. It expires in 10 minutes.
              </p>
              <form onSubmit={(e) => { e.preventDefault(); submitReset(); }} className="mt-6 space-y-3">
                <input
                  data-testid="fp-otp"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={fp.otp}
                  onChange={(e) => setFp({ ...fp, otp: e.target.value.replace(/\D/g, "") })}
                  placeholder="6-digit code"
                  className="w-full rounded-2xl border border-border bg-white px-5 py-3.5 text-sm text-center tracking-[0.5em] font-semibold outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-[border-color,box-shadow]"
                />
                <PasswordField
                  testid="fp-new-password"
                  value={fp.new_password}
                  onChange={(e) => setFp({ ...fp, new_password: e.target.value })}
                  placeholder="New password (min 6 chars)"
                  autoComplete="new-password"
                />
                <button
                  data-testid="fp-reset-btn"
                  type="submit"
                  disabled={fpLoading}
                  className="w-full rounded-full bg-primary text-white py-3.5 text-sm font-medium hover:bg-[#0d5f57] transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {fpLoading ? <Loader2 size={16} className="animate-spin" /> : "Set new password"}
                </button>
              </form>
              <button
                data-testid="fp-resend"
                type="button"
                onClick={() => setAuthView("forgot")}
                className="mt-4 w-full text-center text-xs text-foreground/50 hover:text-primary font-medium transition-colors"
              >
                Didn't get it? Send again
              </button>
            </>
          )}

          <a href="/" className="mt-5 block text-center text-xs text-foreground/50 hover:text-primary transition-colors">
            ← Back to website
          </a>
        </motion.div>
      </div>
    );
  }

  const rows = filteredFor(tab);
  const hasFilter = fromDate || toDate || search;
  const isEditor = me?.role !== "viewer";

  const weekly = (() => {
    const days = [];
    for (let i = 6; i >= 0; i--) {
      const d = new Date();
      d.setHours(0, 0, 0, 0);
      d.setDate(d.getDate() - i);
      const next = new Date(d);
      next.setDate(d.getDate() + 1);
      const inDay = (arr) => arr.filter((r) => { const t = new Date(r.created_at); return t >= d && t < next; }).length;
      days.push({ label: d.toLocaleDateString(undefined, { weekday: "short" }), enq: inDay(enquiries), app: inDay(applications) });
    }
    return days;
  })();
  const weekMax = Math.max(1, ...weekly.map((d) => Math.max(d.enq, d.app)));
  const inputCls = "w-full rounded-xl border border-border bg-white px-4 py-2.5 text-sm outline-none focus:border-primary transition-colors";

  const NAV = [
    ["leads", "Leads", Inbox],
    ["services", "Services", Layers],
    ["team", "Team", UserRound],
    ["testimonials", "Testimonials", MessageSquareQuote],
    ["careers", "Careers", Users],
    ["contact", "Contact", Mail],
    ...(isEditor ? [["invoices", "Invoices", ReceiptText], ["content", "Content", FileText], ["appearance", "Appearance", Palette]] : []),
    ["users", "Users", ShieldCheck],
    ["activity", "Activity", History],
  ];

  const STATUSES = ["New", "Reviewed", "Hired"];
  const ENQ_STATUSES = ["New", "Reviewed", "Closed"];
  const statusStyle = (s) =>
    s === "Hired" || s === "Closed"
      ? "bg-primary/15 text-primary"
      : s === "Reviewed"
      ? "bg-accent/15 text-accent"
      : "bg-muted text-foreground/60";

  return (
    <div className="min-h-screen bg-muted/40">
      <header className="bg-white border-b border-border sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-6 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Peoplelab" className="h-8 w-auto" />
            <span className="hidden sm:inline text-sm text-foreground/50 border-l border-border pl-3">Admin</span>
          </div>
          <button data-testid="admin-logout" onClick={logout} className="inline-flex items-center gap-2 text-sm text-foreground/70 hover:text-primary transition-colors">
            {me ? (
              <span data-testid="current-user" className="hidden sm:inline-flex items-center gap-2 text-foreground/50">
                @{me.username}
                <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${me.role === "viewer" ? "bg-accent/15 text-accent" : "bg-secondary/20 text-primary"}`}>{me.role || "admin"}</span>
              </span>
            ) : null}
            <LogOut size={16} /> Sign out
          </button>
        </div>
        <div className="max-w-6xl mx-auto px-6 flex gap-1 -mb-px">
          {NAV.map(([id, label, Icon]) => (
            <button
              key={id}
              data-testid={`nav-${id}`}
              onClick={() => goSection(id)}
              className={`inline-flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                section === id ? "border-primary text-primary" : "border-transparent text-foreground/55 hover:text-foreground"
              }`}
            >
              <Icon size={16} /> {label}
            </button>
          ))}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        {section === "leads" && (
          <>
            <div className="grid grid-cols-2 gap-4 sm:gap-6 mb-8">
              {[["Enquiries", stats.enquiries, Mail], ["Applications", stats.applications, Briefcase]].map(([label, val, Icon]) => (
                <div key={label} className="bg-card border border-border rounded-3xl p-6 flex items-center gap-4">
                  <span className="grid place-items-center h-12 w-12 rounded-2xl bg-secondary/15 text-primary"><Icon size={20} /></span>
                  <div>
                    <div className="font-heading text-3xl font-semibold text-foreground">{val}</div>
                    <div className="text-sm text-foreground/55">{label}</div>
                  </div>
                </div>
              ))}
            </div>

            {isEditor && invStats && (
              <div className="mb-8" data-testid="invoice-dashboard">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6">
                  {[
                    ["Outstanding", invStats.outstanding, "text-amber-600", "bg-amber-100"],
                    ["Paid", invStats.paid, "text-green-600", "bg-green-100"],
                    ["Overdue", invStats.overdue, "text-red-600", "bg-red-100"],
                    ["Invoices", invStats.total_invoices, "text-primary", "bg-secondary/15", true],
                  ].map(([label, val, color, bg, isCount]) => (
                    <div key={label} className="bg-card border border-border rounded-3xl p-5" data-testid={`inv-stat-${label.toLowerCase()}`}>
                      <div className={`inline-grid place-items-center h-9 w-9 rounded-xl mb-3 ${bg} ${color}`}><ReceiptText size={17} /></div>
                      <div className={`font-heading text-2xl font-semibold ${isCount ? "text-foreground" : color}`}>
                        {isCount ? val : `₹${Number(val || 0).toLocaleString("en-IN")}`}
                      </div>
                      <div className="text-sm text-foreground/55">{label}</div>
                    </div>
                  ))}
                </div>
                <div className="bg-card border border-border rounded-3xl p-6" data-testid="revenue-chart">
                  <h3 className="font-heading text-lg font-medium mb-5">Revenue (last 6 months · paid)</h3>
                  {(() => {
                    const max = Math.max(1, ...invStats.monthly.map((m) => m.revenue));
                    return (
                      <div className="flex items-end justify-between gap-3 h-40">
                        {invStats.monthly.map((m, i) => (
                          <div key={i} data-testid={`rev-month-${i}`} className="flex-1 flex flex-col items-center gap-2">
                            <div className="w-full flex items-end justify-center h-full">
                              <div title={`₹${m.revenue.toLocaleString("en-IN")}`}
                                className="w-2/3 max-w-[40px] rounded-t-md bg-primary transition-[height] duration-500"
                                style={{ height: `${(m.revenue / max) * 100}%`, minHeight: m.revenue ? "6px" : "2px" }} />
                            </div>
                            <span className="text-xs text-foreground/50">{m.label}</span>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}

            <div data-testid="weekly-chart" className="bg-card border border-border rounded-3xl p-6 mb-8">
              <div className="flex items-center justify-between mb-5">
                <h3 className="font-heading text-lg font-medium">Last 7 days</h3>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-4 text-xs text-foreground/60">
                    <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-primary" /> Enquiries</span>
                    <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-accent" /> Applications</span>
                  </div>
                  <button
                    data-testid="send-digest"
                    onClick={sendDigest}
                    disabled={sendingDigest || !isEditor}
                    title={!isEditor ? "Read-only account" : ""}
                    className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-3.5 py-1.5 text-xs font-medium text-foreground/70 hover:text-primary hover:border-primary/40 transition-colors disabled:opacity-40"
                  >
                    {sendingDigest ? <Loader2 size={13} className="animate-spin" /> : <Mail size={13} />} Email me this summary
                  </button>
                </div>
              </div>
              <div className="flex items-end justify-between gap-3 h-40">
                {weekly.map((d, i) => (
                  <div key={i} data-testid={`chart-day-${i}`} className="flex-1 flex flex-col items-center gap-2">
                    <div className="w-full flex items-end justify-center gap-1.5 h-full">
                      <div title={`${d.enq} enquiries`} className="w-1/2 max-w-[22px] rounded-t-md bg-primary transition-[height] duration-500" style={{ height: `${(d.enq / weekMax) * 100}%`, minHeight: d.enq ? "6px" : "2px" }} />
                      <div title={`${d.app} applications`} className="w-1/2 max-w-[22px] rounded-t-md bg-accent transition-[height] duration-500" style={{ height: `${(d.app / weekMax) * 100}%`, minHeight: d.app ? "6px" : "2px" }} />
                    </div>
                    <span className="text-xs text-foreground/50">{d.label}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
              <div className="flex gap-2">
                {[["enquiries", "Enquiries"], ["applications", "Applications"]].map(([id, label]) => (
                  <button key={id} data-testid={`tab-${id}`} onClick={() => setTab(id)}
                    className={`rounded-full px-5 py-2 text-sm font-medium transition-colors ${tab === id ? "bg-primary text-white" : "bg-white border border-border text-foreground/70 hover:text-primary"}`}>
                    {label}
                  </button>
                ))}
              </div>
              <button data-testid="export-csv" onClick={exportCsv} className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-4 py-2 text-sm font-medium text-foreground/70 hover:text-primary hover:border-primary/40 transition-colors">
                <Download size={15} /> Export CSV
              </button>
            </div>

            <div className="flex flex-wrap items-center gap-3 mb-6 bg-white border border-border rounded-2xl px-4 py-3">
              <div className="relative flex-1 min-w-[200px]">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground/40" />
                <input data-testid="lead-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, email, keyword…"
                  className="w-full rounded-lg border border-border bg-white pl-9 pr-3 py-2 text-sm outline-none focus:border-primary transition-colors" />
              </div>
              <div className="flex items-center gap-2">
                <label className="text-xs text-foreground/55">From</label>
                <input data-testid="filter-from" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="rounded-lg border border-border bg-white px-3 py-1.5 text-sm outline-none focus:border-primary transition-colors" />
              </div>
              <div className="flex items-center gap-2">
                <label className="text-xs text-foreground/55">To</label>
                <input data-testid="filter-to" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="rounded-lg border border-border bg-white px-3 py-1.5 text-sm outline-none focus:border-primary transition-colors" />
              </div>
              {hasFilter && (
                <button data-testid="filter-clear" onClick={() => { setFromDate(""); setToDate(""); setSearch(""); }} className="text-sm text-primary hover:underline">Clear</button>
              )}
              <span className="ml-auto text-sm text-foreground/55">{rows.length} {tab}</span>
            </div>

            {tab === "applications" && (
              <div className="flex flex-wrap gap-2 mb-6">
                {["all", ...STATUSES].map((s) => (
                  <button
                    key={s}
                    data-testid={`status-filter-${s}`}
                    onClick={() => setStatusFilter(s)}
                    className={`rounded-full px-4 py-1.5 text-xs font-medium transition-colors ${
                      statusFilter === s ? "bg-primary text-white" : "bg-white border border-border text-foreground/60 hover:text-primary"
                    }`}
                  >
                    {s === "all" ? "All" : s}
                  </button>
                ))}
              </div>
            )}

            {tab === "enquiries" && (
              <div className="flex flex-wrap gap-2 mb-6">
                {["all", ...ENQ_STATUSES].map((s) => (
                  <button
                    key={s}
                    data-testid={`enq-status-filter-${s}`}
                    onClick={() => setEnqStatusFilter(s)}
                    className={`rounded-full px-4 py-1.5 text-xs font-medium transition-colors ${
                      enqStatusFilter === s ? "bg-primary text-white" : "bg-white border border-border text-foreground/60 hover:text-primary"
                    }`}
                  >
                    {s === "all" ? "All" : s}
                  </button>
                ))}
              </div>
            )}

            <div className="bg-card border border-border rounded-3xl overflow-hidden">
              {rows.length === 0 ? (
                <div className="py-20 text-center text-foreground/50"><Inbox className="mx-auto mb-3" size={28} />No {tab} match your view.</div>
              ) : (
                <div className="divide-y divide-border">
                  {rows.map((r) => (
                    <div key={r.id} data-testid={`row-${r.id}`} className="p-6 flex flex-col sm:flex-row sm:items-start gap-3">
                      <div className="sm:w-48 shrink-0">
                        <div className="font-heading font-medium text-foreground flex items-center gap-1.5">
                          {r.name}
                          {(tab === "applications" || tab === "enquiries") && r.notes ? (
                            <StickyNote size={14} className="text-accent" data-testid={`notes-icon-${r.id}`} title="Has notes" />
                          ) : null}
                        </div>
                        <a href={`mailto:${r.email}`} className="text-sm text-primary hover:underline break-all">{r.email}</a>
                        <div className="text-xs text-foreground/45 mt-1">{fmt(r.created_at)}</div>
                      </div>
                      <div className="flex-1">
                        {tab === "enquiries" ? (
                          <>
                            <div className="flex items-center gap-3 flex-wrap mb-2">
                              {r.company ? <span className="text-xs px-2.5 py-1 rounded-full bg-muted text-foreground/70">{r.company}</span> : null}
                              <span className="text-xs px-2.5 py-1 rounded-full bg-secondary/15 text-primary">{r.service}</span>
                              <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${statusStyle(r.status || "New")}`}>{r.status || "New"}</span>
                              {isEditor && (
                                <select
                                  data-testid={`enq-status-${r.id}`}
                                  value={r.status || "New"}
                                  onChange={(e) => updateEnquiryStatus(r.id, e.target.value)}
                                  className="ml-auto rounded-lg border border-border bg-white px-3 py-1.5 text-xs outline-none focus:border-primary transition-colors"
                                >
                                  {ENQ_STATUSES.map((s) => (
                                    <option key={s} value={s}>{s}</option>
                                  ))}
                                </select>
                              )}
                            </div>
                            <p className="text-sm text-foreground/75 leading-relaxed">{r.message}</p>
                            <div className="mt-3">
                              <label className="text-xs text-foreground/45">Private notes</label>
                              <textarea
                                data-testid={`enq-notes-${r.id}`}
                                defaultValue={r.notes || ""}
                                readOnly={!isEditor}
                                onBlur={(e) => { if (isEditor && e.target.value !== (r.notes || "")) saveEnquiryNotes(r.id, e.target.value); }}
                                placeholder={isEditor ? "Add a private note (saves when you click away)…" : "No notes"}
                                rows={2}
                                className="mt-1 w-full rounded-xl border border-border bg-white px-3 py-2 text-sm outline-none focus:border-primary transition-colors resize-none read-only:bg-muted/40"
                              />
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="flex items-center gap-3 flex-wrap">
                              <span className="text-xs px-2.5 py-1 rounded-full bg-secondary/15 text-primary font-medium">{r.role}</span>
                              <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${statusStyle(r.status || "New")}`}>{r.status || "New"}</span>
                              {isEditor && (
                                <select
                                  data-testid={`status-${r.id}`}
                                  value={r.status || "New"}
                                  onChange={(e) => updateStatus(r.id, e.target.value)}
                                  className="ml-auto rounded-lg border border-border bg-white px-3 py-1.5 text-xs outline-none focus:border-primary transition-colors"
                                >
                                  {STATUSES.map((s) => (
                                    <option key={s} value={s}>{s}</option>
                                  ))}
                                </select>
                              )}
                            </div>
                            {r.resume_url ? (
                              <a
                                data-testid={`resume-download-${r.id}`}
                                href={`${process.env.REACT_APP_BACKEND_URL}${r.resume_url}?download=1`}
                                target="_blank"
                                rel="noreferrer"
                                className="mt-3 inline-flex items-center gap-2 rounded-full bg-primary/10 text-primary px-4 py-2 text-xs font-medium hover:bg-primary hover:text-white transition-colors"
                              >
                                <Download size={14} /> {r.resume_name || "Download résumé"}
                              </a>
                            ) : (
                              <div className="mt-3 text-xs text-foreground/40">No résumé attached</div>
                            )}
                            <div className="mt-3">
                              <label className="text-xs text-foreground/45">Private notes</label>
                              <textarea
                                data-testid={`notes-${r.id}`}
                                defaultValue={r.notes || ""}
                                readOnly={!isEditor}
                                onBlur={(e) => { if (isEditor && e.target.value !== (r.notes || "")) saveNotes(r.id, e.target.value); }}
                                placeholder={isEditor ? "Add a private note (saves when you click away)…" : "No notes"}
                                rows={2}
                                className="mt-1 w-full rounded-xl border border-border bg-white px-3 py-2 text-sm outline-none focus:border-primary transition-colors resize-none read-only:bg-muted/40"
                              />
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {section === "services" && (
          <>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-2xl font-medium">Services</h2>
              {isEditor && (
                <button data-testid="add-service" onClick={() => setEditing({ type: "service", data: { no: "", title: "", desc: "", tags: "", image: "", order: services.length } })}
                  className="inline-flex items-center gap-2 rounded-full bg-primary text-white px-5 py-2.5 text-sm font-medium hover:bg-[#0d5f57] transition-colors">
                  <Plus size={16} /> Add service
                </button>
              )}
            </div>
            <p className="text-xs text-foreground/45 mb-3">Drag the handle to reorder — changes save automatically.</p>
            <Reorder.Group axis="y" values={services} onReorder={(v) => { setServices(v); persistOrder("service", v); }} className="space-y-4">
              {services.map((s) => (
                <Reorder.Item key={s.id} value={s} data-testid={`service-row-${s.id}`} className="bg-card border border-border rounded-3xl overflow-hidden">
                  <div className="flex">
                    <span className={`px-3 text-foreground/30 ${isEditor ? "cursor-grab active:cursor-grabbing hover:text-primary" : "opacity-30"}`}><GripVertical size={20} /></span>
                    {s.image ? <img src={s.image} alt={s.title} className="w-40 h-auto object-cover self-stretch" /> : null}
                    <div className="p-6 flex-1">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <span className="text-xs font-bold text-foreground/40">{s.no}</span>
                          <h3 className="font-heading text-lg font-medium">{s.title}</h3>
                        </div>
                        {isEditor && (
                          <div className="flex gap-2 shrink-0">
                            <button data-testid={`edit-service-${s.id}`} onClick={() => setEditing({ type: "service", data: { ...s, tags: (s.tags || []).join(", ") } })} className="grid place-items-center h-9 w-9 rounded-full border border-border text-foreground/60 hover:text-primary hover:border-primary/40 transition-colors"><Pencil size={15} /></button>
                            <button data-testid={`del-service-${s.id}`} onClick={() => deleteItem("service", s.id)} className="grid place-items-center h-9 w-9 rounded-full border border-border text-foreground/60 hover:text-red-500 hover:border-red-300 transition-colors"><Trash2 size={15} /></button>
                          </div>
                        )}
                      </div>
                      <p className="mt-2 text-sm text-foreground/70 leading-relaxed">{s.desc}</p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {(s.tags || []).map((t) => <span key={t} className="text-xs px-2.5 py-1 rounded-full bg-secondary/15 text-primary">{t}</span>)}
                      </div>
                    </div>
                  </div>
                </Reorder.Item>
              ))}
            </Reorder.Group>
            {services.length === 0 && <p className="text-foreground/50">No services yet.</p>}
          </>
        )}

        {section === "team" && (
          <>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-2xl font-medium">Meet the team</h2>
              {isEditor && (
                <button data-testid="add-team" onClick={() => setEditing({ type: "team", data: { name: "", role: "", bio: "", image: "", order: team.length } })}
                  className="inline-flex items-center gap-2 rounded-full bg-primary text-white px-5 py-2.5 text-sm font-medium hover:bg-[#0d5f57] transition-colors">
                  <Plus size={16} /> Add member
                </button>
              )}
            </div>
            <p className="text-xs text-foreground/45 mb-3">Drag the handle to reorder — changes save automatically.</p>
            <Reorder.Group axis="y" values={team} onReorder={(v) => { setTeam(v); persistOrder("team", v); }} className="space-y-4">
              {team.map((m) => (
                <Reorder.Item key={m.id} value={m} data-testid={`team-row-${m.id}`} className="bg-card border border-border rounded-3xl p-5">
                  <div className="flex items-center gap-4">
                    <span className={`text-foreground/30 ${isEditor ? "cursor-grab active:cursor-grabbing hover:text-primary" : "opacity-30"}`}><GripVertical size={20} /></span>
                    {m.image ? (
                      <img src={m.image} alt={m.name} className="h-14 w-14 rounded-2xl object-cover border border-border" />
                    ) : (
                      <span className="grid place-items-center h-14 w-14 rounded-2xl bg-secondary/15 text-primary font-heading font-semibold">{m.name.split(" ").map((n) => n[0]).slice(0, 2).join("")}</span>
                    )}
                    <div className="flex-1">
                      <div className="font-heading text-lg font-medium">{m.name}</div>
                      <div className="text-sm text-accent font-medium">{m.role}</div>
                      {m.bio ? <p className="text-sm text-foreground/60 mt-1 line-clamp-1">{m.bio}</p> : null}
                    </div>
                    {isEditor && (
                      <div className="flex gap-2 shrink-0">
                        <button data-testid={`edit-team-${m.id}`} onClick={() => setEditing({ type: "team", data: { ...m } })} className="grid place-items-center h-9 w-9 rounded-full border border-border text-foreground/60 hover:text-primary hover:border-primary/40 transition-colors"><Pencil size={15} /></button>
                        <button data-testid={`del-team-${m.id}`} onClick={() => deleteItem("team", m.id)} className="grid place-items-center h-9 w-9 rounded-full border border-border text-foreground/60 hover:text-red-500 hover:border-red-300 transition-colors"><Trash2 size={15} /></button>
                      </div>
                    )}
                  </div>
                </Reorder.Item>
              ))}
            </Reorder.Group>
            {team.length === 0 && <p className="text-foreground/50">No team members yet.</p>}
          </>
        )}

        {section === "testimonials" && (
          <>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-2xl font-medium">Testimonials</h2>
              {isEditor && (
                <button data-testid="add-testimonial" onClick={() => setEditing({ type: "testimonial", data: { quote: "", name: "", role: "", image: "", featured: false, order: testimonials.length } })}
                  className="inline-flex items-center gap-2 rounded-full bg-primary text-white px-5 py-2.5 text-sm font-medium hover:bg-[#0d5f57] transition-colors">
                  <Plus size={16} /> Add testimonial
                </button>
              )}
            </div>
            <p className="text-xs text-foreground/45 mb-3" data-testid="featured-hint">
              {testimonials.filter((t) => t.featured).length} featured · the homepage shows the first 3 featured (drag to reorder them).
            </p>
            <p className="text-xs text-foreground/45 mb-3">Drag the handle to reorder — changes save automatically.</p>
            <Reorder.Group axis="y" values={testimonials} onReorder={(v) => { setTestimonials(v); persistOrder("testimonial", v); }} className="space-y-4">
              {testimonials.map((t) => (
                <Reorder.Item key={t.id} value={t} data-testid={`testi-row-${t.id}`} className="bg-card border border-border rounded-3xl p-6">
                  <div className="flex gap-3">
                    <span className={`pt-1 text-foreground/30 ${isEditor ? "cursor-grab active:cursor-grabbing hover:text-primary" : "opacity-30"}`}><GripVertical size={20} /></span>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="flex text-accent">{Array.from({ length: 5 }).map((_, s) => <Star key={s} size={13} fill="currentColor" />)}</div>
                        {t.featured ? <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-primary/15 text-primary">Featured</span> : null}
                      </div>
                      <p className="text-sm text-foreground/80 leading-relaxed">"{t.quote}"</p>
                      <div className="mt-4 flex items-end justify-between">
                        <div className="flex items-center gap-3">
                          {t.image ? <img src={t.image} alt={t.name} className="h-10 w-10 rounded-full object-cover border border-border" /> : null}
                          <div>
                            <div className="font-heading font-medium">{t.name}</div>
                            <div className="text-sm text-foreground/55">{t.role}</div>
                          </div>
                        </div>
                        {isEditor && (
                          <div className="flex gap-2">
                            <button data-testid={`feature-testi-${t.id}`} onClick={() => toggleFeatured(t)} title="Toggle featured" className={`grid place-items-center h-9 w-9 rounded-full border transition-colors ${t.featured ? "border-primary text-primary bg-primary/10" : "border-border text-foreground/60 hover:text-accent hover:border-accent/40"}`}><Star size={15} fill={t.featured ? "currentColor" : "none"} /></button>
                            <button data-testid={`edit-testi-${t.id}`} onClick={() => setEditing({ type: "testimonial", data: { ...t } })} className="grid place-items-center h-9 w-9 rounded-full border border-border text-foreground/60 hover:text-primary hover:border-primary/40 transition-colors"><Pencil size={15} /></button>
                            <button data-testid={`del-testi-${t.id}`} onClick={() => deleteItem("testimonial", t.id)} className="grid place-items-center h-9 w-9 rounded-full border border-border text-foreground/60 hover:text-red-500 hover:border-red-300 transition-colors"><Trash2 size={15} /></button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </Reorder.Item>
              ))}
            </Reorder.Group>
            {testimonials.length === 0 && <p className="text-foreground/50">No testimonials yet.</p>}
          </>
        )}

        {section === "careers" && (
          <>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-2xl font-medium">Career postings</h2>
              {isEditor && (
                <button data-testid="add-job" onClick={() => setEditing({ type: "job", data: { role: "", location: "", type: "Full-time", order: jobs.length } })}
                  className="inline-flex items-center gap-2 rounded-full bg-primary text-white px-5 py-2.5 text-sm font-medium hover:bg-[#0d5f57] transition-colors">
                  <Plus size={16} /> Add role
                </button>
              )}
            </div>
            <p className="text-xs text-foreground/45 mb-3">Drag the handle to reorder — changes save automatically.</p>
            <Reorder.Group axis="y" values={jobs} onReorder={(v) => { setJobs(v); persistOrder("job", v); }} className="bg-card border border-border rounded-3xl divide-y divide-border overflow-hidden">
              {jobs.map((j) => (
                <Reorder.Item key={j.id} value={j} data-testid={`job-row-${j.id}`} className="p-6 flex items-center justify-between gap-4 bg-card">
                  <div className="flex items-center gap-3">
                    <span className={`text-foreground/30 ${isEditor ? "cursor-grab active:cursor-grabbing hover:text-primary" : "opacity-30"}`}><GripVertical size={20} /></span>
                    <div>
                      <div className="font-heading text-lg font-medium">{j.role}</div>
                      <div className="text-sm text-foreground/55">{j.location} · {j.type}</div>
                    </div>
                  </div>
                  {isEditor && (
                    <div className="flex gap-2">
                      <button data-testid={`edit-job-${j.id}`} onClick={() => setEditing({ type: "job", data: { ...j } })} className="grid place-items-center h-9 w-9 rounded-full border border-border text-foreground/60 hover:text-primary hover:border-primary/40 transition-colors"><Pencil size={15} /></button>
                      <button data-testid={`del-job-${j.id}`} onClick={() => deleteItem("job", j.id)} className="grid place-items-center h-9 w-9 rounded-full border border-border text-foreground/60 hover:text-red-500 hover:border-red-300 transition-colors"><Trash2 size={15} /></button>
                    </div>
                  )}
                </Reorder.Item>
              ))}
            </Reorder.Group>
            {jobs.length === 0 && <div className="p-6 text-foreground/50">No roles posted.</div>}
          </>
        )}
        {section === "users" && (
          <>
            <div className="flex items-center gap-3 mb-6">
              <span className="grid place-items-center h-11 w-11 rounded-2xl bg-primary/10 text-primary"><ShieldCheck size={20} /></span>
              <div>
                <h2 className="font-heading text-2xl font-medium">Users & account</h2>
                <p className="text-sm text-foreground/55">Admins manage the whole site; viewers can only read.</p>
              </div>
            </div>

            <div className="bg-card border border-border rounded-3xl p-6 mb-6">
              <h3 className="font-heading text-lg font-medium mb-4 flex items-center gap-2"><Lock size={18} /> Change my password</h3>
              <div className="flex flex-col sm:flex-row gap-3">
                <PasswordField testid="cpw-current" placeholder="Current password" value={cpw.current_password}
                  onChange={(e) => setCpw({ ...cpw, current_password: e.target.value })}
                  autoComplete="current-password" className="flex-1"
                  inputClass="w-full rounded-xl border border-border bg-white px-4 py-2.5 text-sm outline-none focus:border-primary transition-colors" />
                <PasswordField testid="cpw-new" placeholder="New password (min 6 chars)" value={cpw.new_password}
                  onChange={(e) => setCpw({ ...cpw, new_password: e.target.value })}
                  autoComplete="new-password" className="flex-1"
                  inputClass="w-full rounded-xl border border-border bg-white px-4 py-2.5 text-sm outline-none focus:border-primary transition-colors" />
                <button data-testid="change-password-btn" onClick={changeMyPassword} disabled={changingPw}
                  className="rounded-full bg-primary text-white px-6 py-2.5 text-sm font-medium hover:bg-[#0d5f57] transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60">
                  {changingPw ? <Loader2 size={15} className="animate-spin" /> : "Update password"}
                </button>
              </div>
            </div>

            {!isEditor && (
              <div data-testid="viewer-note" className="bg-secondary/10 border border-secondary/30 rounded-2xl px-5 py-4 text-sm text-primary">
                You have <b>read-only (viewer)</b> access. You can browse leads and content but can't make changes or manage users.
              </div>
            )}

            {isEditor && (
            <>
            <div className="bg-card border border-border rounded-3xl p-6 mb-6">
              <h3 className="font-heading text-lg font-medium mb-4 flex items-center gap-2"><UserPlus size={18} /> Create a new user</h3>
              <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3">
                <input data-testid="new-user-username" placeholder="Username" value={newUser.username}
                  onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                  className="flex-1 min-w-[140px] rounded-xl border border-border bg-white px-4 py-2.5 text-sm outline-none focus:border-primary transition-colors" />
                <input data-testid="new-user-email" type="email" placeholder="Email (for password reset)" value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  className="flex-1 min-w-[180px] rounded-xl border border-border bg-white px-4 py-2.5 text-sm outline-none focus:border-primary transition-colors" />
                <PasswordField testid="new-user-password" placeholder="Password (min 6 chars)" value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  autoComplete="new-password" className="flex-1 min-w-[160px]"
                  inputClass="w-full rounded-xl border border-border bg-white px-4 py-2.5 text-sm outline-none focus:border-primary transition-colors" />
                <select data-testid="new-user-role" value={newUser.role}
                  onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                  className="rounded-xl border border-border bg-white px-4 py-2.5 text-sm outline-none focus:border-primary transition-colors">
                  <option value="admin">Admin (full access)</option>
                  <option value="viewer">Viewer (read-only)</option>
                </select>
                <button data-testid="create-user-btn" onClick={createUser} disabled={creatingUser}
                  className="rounded-full bg-primary text-white px-6 py-2.5 text-sm font-medium hover:bg-[#0d5f57] transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60">
                  {creatingUser ? <Loader2 size={15} className="animate-spin" /> : "Create user"}
                </button>
              </div>
            </div>

            <div className="bg-card border border-border rounded-3xl divide-y divide-border overflow-hidden">
              {users.map((u) => (
                <div key={u.id} data-testid={`user-row-${u.id}`} className="p-5 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <span className="grid place-items-center h-10 w-10 rounded-full bg-secondary/15 text-primary font-heading font-semibold uppercase">{u.username.slice(0, 2)}</span>
                    <div>
                      <div className="font-heading font-medium flex items-center gap-2">
                        @{u.username}
                        <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${u.role === "viewer" ? "bg-accent/15 text-accent" : "bg-secondary/20 text-primary"}`}>{u.role || "admin"}</span>
                        {me && u.id === me.id ? <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-primary/15 text-primary">You</span> : null}
                      </div>
                      {emailEdit && emailEdit.id === u.id ? (
                        <div className="mt-1.5 flex items-center gap-2">
                          <input data-testid={`email-input-${u.id}`} type="email" value={emailEdit.value} autoFocus
                            onChange={(e) => setEmailEdit({ ...emailEdit, value: e.target.value })}
                            onKeyDown={(e) => { if (e.key === "Enter") saveUserEmail(); if (e.key === "Escape") setEmailEdit(null); }}
                            placeholder="user@example.com"
                            className="rounded-lg border border-border bg-white px-3 py-1.5 text-xs outline-none focus:border-primary transition-colors w-52" />
                          <button data-testid={`email-save-${u.id}`} onClick={saveUserEmail} disabled={savingEmail}
                            className="grid place-items-center h-7 w-7 rounded-full bg-primary text-white hover:bg-[#0d5f57] transition-colors disabled:opacity-60">
                            {savingEmail ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                          </button>
                          <button data-testid={`email-cancel-${u.id}`} onClick={() => setEmailEdit(null)}
                            className="grid place-items-center h-7 w-7 rounded-full border border-border text-foreground/50 hover:text-red-500 transition-colors"><X size={12} /></button>
                        </div>
                      ) : (
                        <div className="text-xs text-foreground/45 flex items-center gap-1.5">
                          <Mail size={11} />
                          <span data-testid={`user-email-${u.id}`}>{u.email || "No email — needed for password reset"}</span>
                          <button data-testid={`edit-email-${u.id}`} onClick={() => setEmailEdit({ id: u.id, value: u.email || "" })}
                            className="text-primary/70 hover:text-primary transition-colors"><Pencil size={11} /></button>
                          <span className="mx-1 text-foreground/20">·</span>
                          {u.created_at ? new Date(u.created_at).toLocaleDateString() : ""}
                        </div>
                      )}
                    </div>
                  </div>
                  {me && u.id !== me.id ? (
                    <button data-testid={`del-user-${u.id}`} onClick={() => deleteUser(u.id)} className="grid place-items-center h-9 w-9 rounded-full border border-border text-foreground/60 hover:text-red-500 hover:border-red-300 transition-colors"><Trash2 size={15} /></button>
                  ) : (
                    <span className="text-xs text-foreground/40">Current session</span>
                  )}
                </div>
              ))}
            </div>
            </>
            )}
          </>
        )}
        {section === "activity" && (
          <>
            <div className="flex items-center gap-3 mb-6">
              <span className="grid place-items-center h-11 w-11 rounded-2xl bg-primary/10 text-primary"><History size={20} /></span>
              <div>
                <h2 className="font-heading text-2xl font-medium">Activity log</h2>
                <p className="text-sm text-foreground/55">Who changed what, and when. Most recent first.</p>
              </div>
              {isEditor && audit.length > 0 && (
                <button data-testid="clear-audit" onClick={clearAudit}
                  className="ml-auto inline-flex items-center gap-2 rounded-full border border-border bg-white px-4 py-2 text-sm font-medium text-foreground/70 hover:text-red-500 hover:border-red-300 transition-colors">
                  <Trash2 size={15} /> Clear log
                </button>
              )}
            </div>
            <div className="bg-card border border-border rounded-3xl overflow-hidden">
              {audit.length === 0 ? (
                <div className="py-20 text-center text-foreground/50"><History className="mx-auto mb-3" size={28} />No activity recorded yet.</div>
              ) : (
                <div className="divide-y divide-border">
                  {audit.map((l) => (
                    <div key={l.id} data-testid={`audit-${l.id}`} className="p-5 flex items-center gap-4">
                      <span className="grid place-items-center h-9 w-9 rounded-full bg-secondary/15 text-primary font-heading text-xs font-semibold uppercase shrink-0">{(l.username || "?").slice(0, 2)}</span>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-foreground">
                          <span className="font-medium text-primary">@{l.username}</span> — {l.action}
                        </div>
                        <div className="text-xs text-foreground/40 truncate">{l.method} {l.path}</div>
                      </div>
                      <div className="text-xs text-foreground/45 shrink-0">{new Date(l.created_at).toLocaleString()}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
        {section === "appearance" && isEditor && (
          <AppearanceTab auth={auth} />
        )}
        {section === "content" && isEditor && (
          <ContentTab auth={auth} />
        )}
        {section === "invoices" && isEditor && (
          <InvoicesTab auth={auth} />
        )}
        {section === "contact" && (
          <>
            <div className="flex items-center gap-3 mb-6">
              <span className="grid place-items-center h-11 w-11 rounded-2xl bg-primary/10 text-primary"><Mail size={20} /></span>
              <div>
                <h2 className="font-heading text-2xl font-medium">Contact details</h2>
                <p className="text-sm text-foreground/55">These show on the public contact section.</p>
              </div>
            </div>
            <div className="bg-card border border-border rounded-3xl p-6 md:p-8 max-w-2xl">
              <div className="space-y-4">
                {[
                  ["email", "Email", "hello@peoplelabadvisory.com"],
                  ["phone", "Phone", "+91 90000 00000"],
                  ["location", "Location", "Bengaluru · Mumbai · Remote"],
                  ["instagram", "Instagram URL", "https://instagram.com/…"],
                  ["linkedin", "LinkedIn URL", "https://linkedin.com/company/…"],
                  ["whatsapp", "WhatsApp URL", "https://wa.me/919000000000"],
                  ["twitter", "X (Twitter) URL", "https://x.com/…"],
                ].map(([key, label, ph]) => (
                  <div key={key}>
                    <label className="text-xs font-medium text-foreground/60 mb-2 block">{label}</label>
                    <input
                      data-testid={`settings-${key}`}
                      value={settings[key] || ""}
                      readOnly={!isEditor}
                      onChange={(e) => setSettings({ ...settings, [key]: e.target.value })}
                      placeholder={ph}
                      className="w-full rounded-2xl border border-border bg-white px-5 py-3 text-sm outline-none focus:border-primary transition-colors read-only:bg-muted/40"
                    />
                  </div>
                ))}
              </div>
              {isEditor && (
                <button data-testid="save-settings" onClick={saveSettings} disabled={savingSettings}
                  className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary text-white px-6 py-3 text-sm font-medium hover:bg-[#0d5f57] transition-colors disabled:opacity-60">
                  {savingSettings ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save changes
                </button>
              )}
            </div>
          </>
        )}
      </main>

      {/* Edit / Add modal */}
      {editing && (
        <div className="fixed inset-0 z-30 grid place-items-center bg-foreground/40 backdrop-blur-sm px-6" onClick={() => setEditing(null)}>
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} onClick={(e) => e.stopPropagation()}
            className="w-full max-w-lg bg-card rounded-3xl p-7 shadow-2xl" data-testid="edit-modal">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-heading text-xl font-medium">
                {editing.data.id ? "Edit" : "Add"}{" "}
                {editing.type === "testimonial" ? "testimonial" : editing.type === "job" ? "role" : editing.type === "team" ? "member" : "service"}
              </h3>
              <button onClick={() => setEditing(null)} className="text-foreground/50 hover:text-foreground"><X size={20} /></button>
            </div>
            <div className="space-y-4">
              {editing.type === "testimonial" ? (
                <>
                  <textarea data-testid="field-quote" rows={4} placeholder="Quote" value={editing.data.quote}
                    onChange={(e) => setEditing({ ...editing, data: { ...editing.data, quote: e.target.value } })} className={`${inputCls} resize-none`} />
                  <input data-testid="field-name" placeholder="Client name" value={editing.data.name}
                    onChange={(e) => setEditing({ ...editing, data: { ...editing.data, name: e.target.value } })} className={inputCls} />
                  <input data-testid="field-role" placeholder="Role, Company" value={editing.data.role}
                    onChange={(e) => setEditing({ ...editing, data: { ...editing.data, role: e.target.value } })} className={inputCls} />
                  <div className="flex items-center gap-3">
                    <label data-testid="upload-testi-label" className="inline-flex items-center gap-2 rounded-xl border border-border bg-white px-4 py-2.5 text-sm font-medium text-foreground/70 hover:text-primary hover:border-primary/40 transition-colors cursor-pointer">
                      {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} Upload photo
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(e.target.files)} />
                    </label>
                    {editing.data.image ? <img src={editing.data.image} alt="preview" className="h-11 w-11 rounded-full object-cover border border-border" /> : null}
                  </div>
                  <label className="flex items-center gap-2 text-sm text-foreground/70 cursor-pointer">
                    <input data-testid="field-featured" type="checkbox" checked={!!editing.data.featured}
                      onChange={(e) => setEditing({ ...editing, data: { ...editing.data, featured: e.target.checked } })} className="h-4 w-4 accent-[#12776E]" />
                    Feature this testimonial (shows first on the homepage)
                  </label>
                </>
              ) : editing.type === "job" ? (
                <>
                  <input data-testid="field-jobrole" placeholder="Role title" value={editing.data.role}
                    onChange={(e) => setEditing({ ...editing, data: { ...editing.data, role: e.target.value } })} className={inputCls} />
                  <input data-testid="field-location" placeholder="Location (e.g. Bengaluru · Hybrid)" value={editing.data.location}
                    onChange={(e) => setEditing({ ...editing, data: { ...editing.data, location: e.target.value } })} className={inputCls} />
                  <input data-testid="field-type" placeholder="Type (e.g. Full-time)" value={editing.data.type}
                    onChange={(e) => setEditing({ ...editing, data: { ...editing.data, type: e.target.value } })} className={inputCls} />
                  <div className="flex items-center gap-3">
                    <label data-testid="upload-job-label" className="inline-flex items-center gap-2 rounded-xl border border-border bg-white px-4 py-2.5 text-sm font-medium text-foreground/70 hover:text-primary hover:border-primary/40 transition-colors cursor-pointer">
                      {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} Upload image
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(e.target.files)} />
                    </label>
                    {editing.data.image ? <img src={editing.data.image} alt="preview" className="h-10 w-16 rounded-lg object-cover border border-border" /> : null}
                  </div>
                </>
              ) : editing.type === "team" ? (
                <>
                  <input data-testid="field-teamname" placeholder="Full name" value={editing.data.name}
                    onChange={(e) => setEditing({ ...editing, data: { ...editing.data, name: e.target.value } })} className={inputCls} />
                  <input data-testid="field-teamrole" placeholder="Role / title" value={editing.data.role}
                    onChange={(e) => setEditing({ ...editing, data: { ...editing.data, role: e.target.value } })} className={inputCls} />
                  <textarea data-testid="field-teambio" rows={3} placeholder="Short bio (optional)" value={editing.data.bio}
                    onChange={(e) => setEditing({ ...editing, data: { ...editing.data, bio: e.target.value } })} className={`${inputCls} resize-none`} />
                  <div className="flex items-center gap-3">
                    <label data-testid="upload-team-label" className="inline-flex items-center gap-2 rounded-xl border border-border bg-white px-4 py-2.5 text-sm font-medium text-foreground/70 hover:text-primary hover:border-primary/40 transition-colors cursor-pointer">
                      {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} Upload photo
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(e.target.files)} />
                    </label>
                    {editing.data.image ? <img src={editing.data.image} alt="preview" className="h-12 w-12 rounded-full object-cover border border-border" /> : null}
                  </div>
                </>
              ) : (
                <>
                  <input data-testid="field-no" placeholder="Number (e.g. 01)" value={editing.data.no}
                    onChange={(e) => setEditing({ ...editing, data: { ...editing.data, no: e.target.value } })} className={inputCls} />
                  <input data-testid="field-title" placeholder="Service title" value={editing.data.title}
                    onChange={(e) => setEditing({ ...editing, data: { ...editing.data, title: e.target.value } })} className={inputCls} />
                  <textarea data-testid="field-desc" rows={3} placeholder="Description" value={editing.data.desc}
                    onChange={(e) => setEditing({ ...editing, data: { ...editing.data, desc: e.target.value } })} className={`${inputCls} resize-none`} />
                  <input data-testid="field-tags" placeholder="Tags (comma separated)" value={editing.data.tags}
                    onChange={(e) => setEditing({ ...editing, data: { ...editing.data, tags: e.target.value } })} className={inputCls} />
                  <div>
                    <div className="flex items-center gap-3">
                      <label data-testid="upload-image-label" className="inline-flex items-center gap-2 rounded-xl border border-border bg-white px-4 py-2.5 text-sm font-medium text-foreground/70 hover:text-primary hover:border-primary/40 transition-colors cursor-pointer">
                        {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} Upload image
                        <input data-testid="field-image-file" type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(e.target.files)} />
                      </label>
                      {editing.data.image ? <img src={editing.data.image} alt="preview" className="h-10 w-16 object-cover rounded-lg border border-border" /> : null}
                    </div>
                    <input data-testid="field-image" placeholder="…or paste an image URL" value={editing.data.image}
                      onChange={(e) => setEditing({ ...editing, data: { ...editing.data, image: e.target.value } })} className={`${inputCls} mt-3`} />
                  </div>
                </>
              )}
              <input type="number" data-testid="field-order" placeholder="Display order" value={editing.data.order}
                onChange={(e) => setEditing({ ...editing, data: { ...editing.data, order: e.target.value } })} className={inputCls} />
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setEditing(null)} className="rounded-full border border-border px-5 py-2.5 text-sm font-medium text-foreground/70 hover:text-foreground transition-colors">Cancel</button>
              <button data-testid="save-item" onClick={saveItem} className="inline-flex items-center gap-2 rounded-full bg-primary text-white px-6 py-2.5 text-sm font-medium hover:bg-[#0d5f57] transition-colors">
                <Save size={15} /> Save
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};
